import React, {useContext, useEffect, useMemo, useState} from 'react';

import dayjs from "dayjs";
import {Card, Collapse, Tooltip, IconButton, Paper} from "@mui/material";

import Typography from "@mui/material/Typography";
import './weather.css';
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import Box from "@mui/material/Box";
import {PAContext} from "../../../Services/PAContext";




function WeatherDetails(props) {

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();

    const { dataArray } = useContext(PAContext);
    const { Account } = dataArray;

    const [weatherData, setWeatherData] = useState(PA_GlobalObject.Planning.weather || []);
    const [pullWeatherData, setPullWeatherData] = useState(true);

    const eventDates = usePA_GlobalStore.getState()?.PA_GlobalObject?.Planning?.time_table?.dates || [];
    const lodgingList = usePA_GlobalStore.getState()?.PA_GlobalObject?.Planning?.lodging || [];

    const weatherByCityDate = useMemo(() => {
        const map = {};

        weatherData.forEach((entry) => {
            const { city, state, report } = entry;
            report?.days?.forEach(day => {
                const key = `${day.datetime}_${city}_${state}`;
                map[key] = {
                    ...day,
                    city,
                    state,
                };
            });
        });

        return map;
    }, [weatherData]);

    const getUserLodgingSegments = (lodgingList) => {

        return lodgingList
            .map(lodging => ({
                city: lodging['location'].city,
                state: lodging['location'].state,
                dates: lodging.time_table?.dates || [],
            }));
    };

    const userLodgingSegments = useMemo(() => {
        return getUserLodgingSegments(lodgingList, Account.id); // ✅ Now it's being used
    }, [lodgingList, Account.id]);


    function getOrderedCitiesForDate(date, segments) {
        const dateStr = dayjs(date).format('YYYY-MM-DD');

        const cities = [];

        // Gather lodging cities for this date
        segments.forEach(segment => {
            const match = segment.dates.find(d => d.date === dateStr);
            if (match) {
                const cityState = `${segment.city}, ${segment.state}`;
                cities.push({
                    cityState,
                    isCheckOut: !!match.checkout_time,
                    isCheckIn: !!match.checkin_time,
                    isMiddle: !match.checkin_time && !match.checkout_time,
                });
            }
        });

        // Sort lodging cities: checkout first, then middle, then checkin
        cities.sort((a, b) => {
            const getPriority = c =>
                c.isCheckOut ? 0 : c.isMiddle ? 1 : 2;
            return getPriority(a) - getPriority(b);
        });

        // Handle user home city/state
        const firstDate = eventDates[0].date;
        const lastDate = eventDates[eventDates.length - 1].date;

        if ((dateStr === firstDate || dateStr === lastDate) && Account.city && Account.state) {
            const userCityState = `${Account.city}, ${Account.state}`;
            const alreadyIncluded = cities.find(c => c.cityState === userCityState);

            if (!alreadyIncluded) {
                const homeEntry = {
                    cityState: userCityState,
                    isHome: true,
                };

                if (dateStr === firstDate) {
                    // On first date: show home first
                    cities.unshift(homeEntry);
                } else if (dateStr === lastDate) {
                    // On last date: show home last
                    cities.push(homeEntry);
                }
            }
        }

        return cities.map(c => c.cityState);
    }

    // ✅ Function to fetch weather data when needed
    useEffect(() => {
        if (pullWeatherData && weatherData.length === 0) {
            fetchWeatherData();
        }
    }, [pullWeatherData, weatherData.length]);


    // ✅ Regular function to fetch weather data
    async function fetchWeatherData() {
        console.log("Checking if weather report needs to be pulled...");

        // Check if event has dates
        const eventDates = PA_GlobalObject.Planning?.time_table?.dates || [];
        if (eventDates.length === 0) {
            console.warn("Event does not have any dates set. Weather report cannot be pulled.");
            return;
        }

        const eventBeginDate = dayjs(eventDates[0].date);
        const eventEndDate = dayjs(eventDates[eventDates.length - 1].date);
        const now = dayjs();
        const daysUntilBeginDate = eventBeginDate.diff(now, 'day');

        // 🎯 Mode Selection: Live Forecast vs. Historical Statistical Data
        const isHistoricalMode = daysUntilBeginDate > 14;

        console.log(`Event begins on: ${eventBeginDate.format("YYYY-MM-DD")} (in ${daysUntilBeginDate} days)`);
        console.log(`Mode: ${isHistoricalMode ? "HISTORICAL (Statistical)" : "LIVE FORECAST"}`);

        // 🎯 DETERMINE GLOBAL UNIT PREFERENCE BASED ON USER'S HOME
        // If the user lives in the US, use "us" (Imperial) for the whole trip. Otherwise, "metric".
        const userCountry = Account.country?.toLowerCase() || "";
        const globalUnitGroup = (userCountry === "us" || userCountry === "united states") ? "us" : "metric";

        console.log(`User Country: ${userCountry} | Using Units: ${globalUnitGroup}`);

        // Extract valid lodging locations
        const lodgingLocations = PA_GlobalObject.Planning.lodging
            .filter(lodging => lodging.location?.latitude !== 0 && lodging.location?.longitude !== 0);

        if (lodgingLocations.length === 0 && !(Account.city && Account.state)) {
            console.warn("No valid lodging locations or account address found for weather report.");
            return;
        }

        // Construct weather request data
        const _search_data_event_locations = lodgingLocations.map(lodge => {
            let bDate = dayjs(lodge.time_table.dates[0].date);
            let eDate = dayjs(lodge.time_table.dates[lodge.time_table.dates.length - 1].date);

            // 🎯 If in historical mode, shift dates back 1 year to get same-season statistics
            if (isHistoricalMode) {
                bDate = bDate.subtract(1, 'year');
                eDate = eDate.subtract(1, 'year');
            }

            return {
                city: lodge.location.city,
                state: lodge.location.state,
                location_type: lodge.location.latitude && lodge.location.longitude ? "latlng" : "address",
                latitude: lodge.location.latitude,
                longitude: lodge.location.longitude,
                location: lodge.location.address ? `${lodge.location.address} ${lodge.location.city} ${lodge.location.state} ${lodge.location.zip}` : "",
                unit_group: Account.country?.toLowerCase() === "us" ? "us" : "metric",
                begin_date: bDate.format('YYYY-MM-DD'),
                end_date: eDate.format('YYYY-MM-DD'),
                display_date_actual: lodge.time_table.dates[0].date, // Keep original date for mapping back
                isHistorical: isHistoricalMode
            };
        });

        // Add account home location for start and end dates
        if (Account.id && Account.city && Account.state) {
            const homeDates = [eventDates[0].date, eventDates[eventDates.length - 1].date];

            homeDates.forEach(date => {
                let hDate = dayjs(date);
                if (isHistoricalMode) hDate = hDate.subtract(1, 'year');

                _search_data_event_locations.push({
                    city: Account.city,
                    state: Account.state,
                    location_type: "address",
                    location: `${Account.address || ''} ${Account.city} ${Account.state} ${Account.zip || ''}`,
                    unit_group: Account.country?.toLowerCase() === "us" ? "us" : "metric",
                    begin_date: hDate.format('YYYY-MM-DD'),
                    end_date: hDate.format('YYYY-MM-DD'),
                    display_date_actual: date,
                    isHistorical: isHistoricalMode
                });
            });
        }

        console.log("Weather request locations:", _search_data_event_locations);

        const visualCrossingBase = "https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/";
        const timelineService = "timeline/";
        const inEvent = daysUntilBeginDate <= 0 && now.diff(eventEndDate, "days") < 1;
        const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

        const fetchWeatherDataSequentially = async () => {
            const weatherResults = [];

            for (const loc of _search_data_event_locations) {
                const searchLocation = loc.location_type === "address"
                    ? `${loc.location}/`
                    : `${loc.latitude},${loc.longitude}/`;

                const searchBeginDate = `${loc.begin_date}/`;
                const searchEndDate = loc.end_date;

                // In historical mode, we usually just want daily summaries (Statistical)
                // In forecast mode within 14 days, we get hourly if inEvent is true
                const includeData = (inEvent || !isHistoricalMode) ? "" : "&include=days";

                const searchUrl = `${visualCrossingBase}${timelineService}${searchLocation}${searchBeginDate}${searchEndDate}?key=HQTW7H7BS2KSSU4Z3LTWMETCV&contentType=json&unitGroup=${loc.unit_group}${includeData}`;

                console.log(`Fetching ${loc.isHistorical ? 'HISTORICAL' : 'FORECAST'} data: ${searchUrl.replaceAll(" ", "%20")}`);

                try {
                    const response = await fetch(searchUrl.replaceAll(" ", "%20"), { method: 'GET' });
                    if (!response.ok) throw new Error(`HTTP Error ${response.status}`);

                    const data = await response.json();

                    // 🎯 CRITICAL: If historical, we must "patch" the dates in the report
                    // so the frontend mapping logic (which uses the event's current year) still works.
                    if (loc.isHistorical && data.days) {
                        data.days = data.days.map((day, idx) => {
                            // Map the old year's data to the event's actual year
                            const actualDate = dayjs(loc.display_date_actual).add(idx, 'day').format('YYYY-MM-DD');
                            return { ...day, datetime: actualDate, isHistorical: true };
                        });
                    }

                    weatherResults.push({ ...loc, report: data });
                } catch (error) {
                    console.error(`Error fetching weather for ${loc.city}:`, error);
                    weatherResults.push({ ...loc, report: null, error: true });
                }

                await delay(500);
            }
            return weatherResults;
        };

        const finalWeatherData = await fetchWeatherDataSequentially();
        setWeatherData(finalWeatherData);
        setPullWeatherData(false);

        set_PA_GlobalObject((prev) => ({
            ...prev,
            Planning: {
                ...prev.Planning,
                weather: finalWeatherData,
            },
        }));
    }
    async function fetchWeatherDataOLD() {
        console.log("Checking if weather report needs to be pulled...");

        // Check if event has dates
        const eventDates = PA_GlobalObject.Planning?.time_table?.dates || [];
        if (eventDates.length === 0) {
            console.warn("Event does not have any dates set. Weather report cannot be pulled.");
            return;
        }

        const eventBeginDate = dayjs(eventDates[0].date);
        const eventEndDate = dayjs(eventDates[eventDates.length - 1].date);
        const daysUntilBeginDate = eventBeginDate.diff(dayjs(), 'day');

        console.log(`Event begins on: ${eventBeginDate.format("YYYY-MM-DD")} (in ${daysUntilBeginDate} days)`);

        if (daysUntilBeginDate > 14) {
            console.info("Outside the 14-day window. No weather report needed yet.");
            return;
        }

        console.log("Within the 14-day window. Fetching lodging locations...");

        // Extract valid lodging locations
        const lodgingLocations = PA_GlobalObject.Planning.lodging
            .filter(lodging => lodging.location?.latitude !== 0 && lodging.location?.longitude !== 0);

        if (lodgingLocations.length === 0) {
            console.warn("No valid lodging locations found for weather report.");
            return;
        }

        console.log(`Found ${lodgingLocations.length} valid lodging locations.`);

        // Construct weather request data
        const _search_data_event_locations = lodgingLocations.map(lodge => ({
            city: lodge.location.city,
            state: lodge.location.state,
            location_type: lodge.location.latitude && lodge.location.longitude ? "latlng" : "address",
            latitude: lodge.location.latitude,
            longitude: lodge.location.longitude,
            location: lodge.location.address ? `${lodge.location.address} ${lodge.location.city} ${lodge.location.state} ${lodge.location.zip}` : "",
            unit_group: (lodge.location.country.toLowerCase() === "us" || lodge.location.country.toLowerCase() === "united states") ? "us" : "metric",
            begin_date: dayjs(lodge.time_table.dates[0].date).format('YYYY-MM-DD'),
            end_date: dayjs(lodge.time_table.dates[lodge.time_table.dates.length - 1].date).format('YYYY-MM-DD'),
        }));

        //add the users Account address city, state for the weather on the first and last day of the event
        if (Account.id && Account.city && Account.state){

            _search_data_event_locations.push({
                city: Account.city,
                state: Account.state,
                location_type: "address",
                location: `${Account.address} ${Account.city} ${Account.state} ${Account.zip}`,
                unit_group: Account.country?.toLowerCase() === "us" ? "us" : "metric",
                begin_date: dayjs(eventDates[0].date).format('YYYY-MM-DD'),
                end_date: dayjs(eventDates[0].date).format('YYYY-MM-DD'),
            });
            _search_data_event_locations.push({
                city: Account.city,
                state: Account.state,
                location_type: "address",
                location: `${Account.address} ${Account.city} ${Account.state} ${Account.zip}`,
                unit_group: Account.country?.toLowerCase() === "us" ? "us" : "metric",
                begin_date: dayjs(eventDates[eventDates.length - 1].date).format('YYYY-MM-DD'),
                end_date: dayjs(eventDates[eventDates.length - 1].date).format('YYYY-MM-DD'),
            });
        }

        console.log("Weather request locations:", _search_data_event_locations);

        // Construct API URLs
        const visualCrossingBase = "https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/";
        const timelineService = "timeline/";

        const inEvent = eventBeginDate.diff(dayjs(), "days") <= 0 && dayjs().diff(eventEndDate, "days") < 1;

        // Function to delay API calls (prevents rate-limiting)
        const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

        // Function to fetch weather data sequentially
        const fetchWeatherDataSequentially = async () => {
            const weatherData = [];

            for (const location of _search_data_event_locations) {
                const searchLocation = location.location_type === "address"
                    ? `${location.location}/`
                    : `${location.latitude},${location.longitude}/`;

                const searchBeginDate = `${location.begin_date}/`;
                const searchEndDate = location.end_date;
                const includeData = inEvent ? "" : "&include=days";     //if empty then it gets hourly data
//                const includeData = location.location_type === "address" ? "" : "&include=days";    //this will mae it get hourly data for at least first and last day

                const searchUrl = `${visualCrossingBase}${timelineService}${searchLocation}${searchBeginDate}${searchEndDate}?key=HQTW7H7BS2KSSU4Z3LTWMETCV&contentType=json&unitGroup=${location.unit_group}${includeData}`;

                console.log(`Fetching weather data from: ${searchUrl.replaceAll(" ", "%20")}`);

                try {
                    const response = await fetch(searchUrl.replaceAll(" ", "%20"), { method: 'GET' });
                    console.log("RAW Weather API Response:", response);

                    if (!response.ok) {
                        throw new Error(`HTTP Error ${response.status}`);
                    }

                    const data = await response.json();
                    console.log("Weather API Response:", data);

                    weatherData.push({ ...location, report: data });

                } catch (error) {
                    console.error(`Error fetching weather for ${location.city}:`, error);
                    weatherData.push({ ...location, report: null, error: true });
                }

                // **Add a delay to avoid "Too Many Requests" error**
                await delay(500); // Wait a half second before the next request
            }

            return weatherData;
        };

        // Fetch weather data and update state
        const finalWeatherData = await fetchWeatherDataSequentially();

        console.log("finalWeatherData : "+JSON.stringify(finalWeatherData, null, 2));
        setWeatherData(finalWeatherData);

        // Prevent repeated API calls
        setPullWeatherData(false);

        set_PA_GlobalObject((prev) => ({
            ...prev,
            Planning: {
                ...prev.Planning,
                weather: finalWeatherData,
            },
        }));

    }

    const getIconPath = (icon) => {
        return `/weather_icons/${icon || 'default'}.png`; // fallback to default.png if needed
    };


    const TemperatureLine = ({ high, low }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 600 }}>
                {Math.round(high)}°
            </Typography>
            <Box
                sx={{
                    minHeight: 45,
                    width: 6,
                    borderRadius: 4,
                    background: 'linear-gradient(to bottom, #d32f2f, #1976d2)',
                    margin: '4px 0',
                }}
            />
            <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 600 }}>
                {Math.round(low)}°
            </Typography>
        </Box>
    );
    const HourlyTemperatureGraph = ({ hourlyData }) => {
        if (!hourlyData || hourlyData.length === 0) return null;

        const temps = hourlyData.map(h => h.temp);
        const maxTemp = Math.max(...temps);
        const minTemp = Math.min(...temps);
        const tempRange = maxTemp - minTemp || 1;

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    width: '100%',
                    borderTop: '1px solid #e0e0e0',
                    pt: 2,
                }}
            >
                {hourlyData.map((hour, index) => {
                    const height = ((hour.temp - minTemp) / tempRange) * 60 + 20;
                    const time = dayjs(hour.datetime, 'HH:mm:ss').format('h A'); // → "1 PM"

                    const showLabel = index % 4 === 0;

                    return (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: `${100 / 24}%`,
                            }}
                        >
                            {showLabel && (
                                <Typography
                                    variant="caption"
                                    sx={{ fontSize: '10px', color: '#d32f2f'}}
                                >
                                    {Math.round(hour.temp)}°
                                </Typography>
                            )}
                            <Tooltip title={`${time} – ${Math.round(hour.temp)}°`} arrow>
                                <Box
                                    sx={{
                                        width: '80%',
                                        height: `${height}px`,
                                        background: 'linear-gradient(to top, #1976d2, #d32f2f)',
                                        borderRadius: 1,
                                        transition: 'height 0.3s ease',
                                    }}
                                />
                            </Tooltip>
                            {showLabel ? (
                                <Typography
                                    variant="caption"
                                    sx={{ fontSize: '10px', color: '#1976d2', mt: 0.7 }}
                                >
                                    {time}
                                </Typography>
                            ) : (
                                    <Typography
                                    variant="caption"
                                    sx={{ fontSize: '10px', color: '#1976d2', mt: '22px' }}
                                    >
                                        &nbsp;
                                    </Typography>
                                )}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    const [expandedWeather, setExpandedWeather] = useState({});

    const handleRefreshWeather = () => {
        // 1. Clear local state so the UI feels responsive
        setWeatherData([]);
        // 2. Set the trigger to true to kick off the useEffect
        setPullWeatherData(true);
        // 3. Clear the global store so we aren't looking at stale data
        set_PA_GlobalObject((prev) => ({
            ...prev,
            Planning: {
                ...prev.Planning,
                weather: [],
            },
        }));
    };

    useEffect(() => {
        // If the trigger is set to true, go get the data regardless of local length
        if (pullWeatherData) {
            fetchWeatherData();
        }
    }, [pullWeatherData]); // Remove weatherData.length from deps to avoid infinite loops




    return (

        <div className="weather-list-container">

            {/* --- WEATHER LIST HEADER --- */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    boxShadow: '0 4px 24px -6px rgba(0,0,0,0.06)',
                    borderRadius: '12px 12px 0 0',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    {/* Title Area */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                            p: 0.9,
                            borderRadius: '10px',
                            display: 'flex',
                            boxShadow: '0 4px 10px rgba(14, 165, 233, 0.3)'
                        }}>
                            <i className="icon-cloud-sun" style={{ color: 'white', fontSize: '18px' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem', letterSpacing: -0.4 }}>
                                Daily Weather
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <i className="icon-location-pin" style={{ color: '#0ea5e9', fontSize: '10px' }} />
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.65rem' }}>
                                    Local Conditions
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Action Area: Refresh Button */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Tooltip title="Update Forecast">
                            <IconButton
                                size="small"
                                onClick={handleRefreshWeather}
                                disabled={pullWeatherData} // Disable while loading
                                sx={{
                                    bgcolor: '#f1f5f9',
                                    color: '#64748b',
                                    borderRadius: '10px',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        bgcolor: '#e0f2fe',
                                        color: '#0ea5e9',
                                        transform: 'rotate(180deg)' // Nice little UI touch
                                    }
                                }}
                            >
                                {/* Use a spinning animation if loading */}
                                <i className={pullWeatherData ? "icon-cw spinner-animate" : "icon-cw"}
                                   style={{ fontSize: '14px' }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Paper>

            <div
                className={
                    props.showingPageHeader
                        ? "weather-list-items-container with-header"
                        : "weather-list-items-container"
                }
            >
                {eventDates.map((date, index) => {
                    const orderedPlaces = getOrderedCitiesForDate(date.date, userLodgingSegments);
                    const formattedDate = dayjs(date.date).format('YYYY-MM-DD');
                    const daysUntil = dayjs(date.date).diff(dayjs(), 'day');
                    const isLocked = daysUntil > 14;

                    return (
                        <Box key={index} sx={{mb: 3}}>
                            <Typography variant="h6" sx={{mb: 2}}>
                                {dayjs(date.date).format('dddd, MMM D')}
                            </Typography>

                            {orderedPlaces.map((place, i) => {
                                const [city, state] = place.split(',').map(s => s.trim());
                                const weatherKey = `${formattedDate}_${city}_${state}`;
                                const weather = weatherByCityDate[weatherKey];

                                console.log("the weather : ", weather);

                                // 1. Placeholder for data outside the 14-day window (if no historical data fetched)
                                if (!weather && isLocked) {
                                    return (
                                        <Card key={`locked-${i}`} sx={{
                                            mb: 2,
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: '#f9f9f9',
                                            border: '1px dashed #ccc'
                                        }}>
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                <Box>
                                                    <Typography variant="subtitle2"
                                                                sx={{fontWeight: 600, color: 'text.secondary'}}>
                                                        📍 {city}, {state}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.disabled">
                                                        Live forecast available in {daysUntil - 14} days.
                                                    </Typography>
                                                </Box>
                                                <i className="icon-lock" style={{color: '#ccc', fontSize: '20px'}}/>
                                            </Box>
                                        </Card>
                                    );
                                }

                                // 2. Generic "No Data" fallback
                                if (!weather) {
                                    return (
                                        <Typography key={`missing-${i}`} variant="caption" color="text.secondary"
                                                    sx={{display: 'block', mb: 2, ml: 1}}>
                                            No weather data for {city}, {state}
                                        </Typography>
                                    );
                                }

                                const isHourly = Array.isArray(weather?.hours);
                                const isHistorical = weather.isHistorical; // Flag from the updated fetch function

                                // Shared Header / Badge logic
                                const HistoricalBadge = isHistorical ? (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'inline-block',
                                            bgcolor: '#fff3e0',
                                            color: '#e65100',
                                            px: 1,
                                            borderRadius: 1,
                                            fontWeight: 700,
                                            fontSize: '10px',
                                            mb: 0.5,
                                            border: '1px solid #ffe0b2'
                                        }}
                                    >
                                        HISTORICAL AVERAGE
                                    </Typography>
                                ) : null;

                                // Hourly Card layout
                                if (isHourly) {
                                    return (
                                        <Card key={`hourly-${i}`}
                                              sx={{mb: 2, p: 2, position: 'relative', borderRadius: 2}}>
                                            {HistoricalBadge}
                                            <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                📍 {city}, {state}
                                            </Typography>

                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%'
                                            }}>
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <Box component="img" src={getIconPath(weather.icon)}
                                                         alt={weather.icon} sx={{height: 40, width: 40, mr: 1}}/>
                                                    <Box>
                                                        <Typography variant="body2"
                                                                    sx={{fontWeight: 500}}>{weather.conditions}</Typography>
                                                        <Typography variant="caption"
                                                                    color="text.secondary">{weather.description}</Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center'
                                                }}>
                                                    <TemperatureLine high={weather.tempmax} low={weather.tempmin}/>
                                                </Box>
                                            </Box>

                                            <Box sx={{mt: 2}}>
                                                <HourlyTemperatureGraph hourlyData={weather.hours}/>
                                            </Box>

                                            <Box
                                                onClick={() => setExpandedWeather(prev => ({
                                                    ...prev,
                                                    [weatherKey]: !prev[weatherKey]
                                                }))}
                                                sx={{
                                                    mt: 2,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-end',
                                                    gap: 0.5,
                                                    color: 'primary.main',
                                                    fontSize: '12px !important',
                                                    fontWeight: 500,
                                                    userSelect: 'none',
                                                }}
                                            >
                                                <Typography variant="body2" sx={{fontSize: '12px !important'}}>
                                                    {expandedWeather[weatherKey] ? 'Hide hourly' : 'Show hourly'}
                                                </Typography>
                                                <i className={expandedWeather[weatherKey] ? 'icon-chevron-up' : 'icon-chevron-down'}
                                                   style={{fontSize: '16px'}}/>
                                            </Box>

                                            <Collapse in={expandedWeather[weatherKey]}>
                                                <Box mt={2}>
                                                    {(() => {
                                                        // 1. Filter current day down to your 4:00 AM - 11:00 PM (23:00) bounds
                                                        let hoursToRender = weather.hours.filter(hour => {
                                                            const h = parseInt(hour.datetime.split(':')[0], 10);
                                                            return h >= 4 && h <= 23;
                                                        });

                                                        // 2. Look-ahead check: see if the next day exists and shares this location
                                                        const nextDayObj = eventDates[index + 1];
                                                        if (nextDayObj) {
                                                            const nextFormattedDate = dayjs(nextDayObj.date).format('YYYY-MM-DD');
                                                            const nextWeatherKey = `${nextFormattedDate}_${city}_${state}`;
                                                            const nextDayWeather = weatherByCityDate[nextWeatherKey];

                                                            // If next day weather exists for this city/state, borrow its 12:00 AM (0:00) index slot
                                                            if (nextDayWeather && Array.isArray(nextDayWeather.hours) && nextDayWeather.hours.length > 0) {
                                                                const midnightHour = nextDayWeather.hours[0]; // 00:00:00 hour slot entry

                                                                // Duplicate and append a timestamp override marker so dayjs doesn't mistake it for today's midnight
                                                                hoursToRender = [
                                                                    ...hoursToRender,
                                                                    { ...midnightHour, isBorrowedNextDay: true }
                                                                ];
                                                            }
                                                        }

                                                        // 3. Map out the compiled timeline
                                                        return hoursToRender.map((hour, idx) => {
                                                            // Handle text labeling formatting for the appended tomorrow hour slot if present
                                                            const formattedTime = hour.isBorrowedNextDay
                                                                ? "12:00 AM *" // Quick asterisk or label to hint it's next day midnight transition
                                                                : dayjs(hour.datetime, 'HH:mm:ss').format('h:mm A');

                                                            return (
                                                                <Box key={idx} sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'flex-start',
                                                                    justifyContent: 'space-between',
                                                                    mb: 1,
                                                                    px: 1,
                                                                    py: 1,
                                                                    borderRadius: 1,
                                                                    backgroundColor: hour.isBorrowedNextDay ? '#f0fdf4' : '#f9f9f9', // Light green tint for next-day midnight
                                                                    border: hour.isBorrowedNextDay ? '1px dashed #bbf7d0' : 'none'
                                                                }}>
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center',
                                                                        width: 60
                                                                    }}>
                                                                        <Typography variant="caption" sx={{ fontWeight: hour.isBorrowedNextDay ? 700 : 400 }}>
                                                                            {formattedTime}
                                                                        </Typography>
                                                                        <Box component="img"
                                                                             src={getIconPath(hour.icon)}
                                                                             alt={hour.icon}
                                                                             sx={{height: 24, width: 24, mt: 0.5}}/>
                                                                    </Box>
                                                                    <Box sx={{flex: 1, pl: 2}}>
                                                                        <Box sx={{
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center'
                                                                        }}>
                                                                            <Typography variant="body2" sx={{ fontWeight: hour.isBorrowedNextDay ? 600 : 400 }}>
                                                                                {hour.conditions}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.secondary">
                                                                                {Math.round(hour.temp)}°
                                                                            </Typography>
                                                                        </Box>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            💧{hour.precipprob || 0}% • Feels like {Math.round(hour.feelslike)}°
                                                                            {hour.isBorrowedNextDay && " (Next Day)"}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        });
                                                    })()}
                                                </Box>
                                            </Collapse>
                                        </Card>
                                    );
                                }

                                // Daily Summary Card (Standard logic)
                                return (
                                    <Card key={`daily-${i}`} sx={{mb: 2, p: 2, position: 'relative', borderRadius: 2}}>
                                        {HistoricalBadge}
                                        <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                            📍 {city}, {state}
                                        </Typography>
                                        <Box sx={{display: 'flex', alignItems: 'center', pr: 8}}>
                                            <Box component="img" src={getIconPath(weather.icon)} alt={weather.icon}
                                                 sx={{height: 40, width: 40, mr: 1}}/>
                                            <Box>
                                                <Typography variant="body2"
                                                            sx={{fontWeight: 500}}>{weather.conditions}</Typography>
                                                <Typography variant="caption"
                                                            color="text.secondary">{weather.description}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 16,
                                            bottom: 16,
                                            right: 16,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}>
                                            <TemperatureLine high={weather.tempmax} low={weather.tempmin}/>
                                        </Box>
                                    </Card>
                                );
                            })}
                        </Box>
                    );
                })}
            </div>
        </div>
    )
}

export default WeatherDetails;
